import React from 'react';


import Section, { InfoCard, SectionHeader } from 'views/site/Section.js';
import Theme from 'styles/Theme.js';
import Utils from 'files/Utils.js';

/*
 * HowItWorks
 *
 * The Event Positioning System as four steps, in the storefront's card network —
 * the same icon, title and copy unit as the hazards and the guarantees, so the
 * page reads as one thing. The storefront's version of this section numbers
 * nothing and neither does this; the order is carried by reading order, and
 * numbered badges were an ornament rather than information.
 *
 * The technical claims are from the manuals: each sensor repeats the network
 * signal, the radio runs at 905.2 / 913.2 MHz with a tested 200 ft open-area
 * range, up to 18 devices per network, and a remotely triggered sensor exits
 * alarm after five minutes to conserve its battery.
 */

const HowItWorks = ({ showProducts = true }) => {

    const mobile = Utils.isMobile();

    const steps = [{
        copy: 'One sensor picks up smoke, heat, carbon monoxide or water — watching for the thing that room is actually likely to produce, which is why a kitchen gets heat and a bedroom gets smoke.',
        icon: 'broadcast',
        title: 'Detect'
    },{
        copy: 'It tells every other device on the network in a fraction of a second. The whole house sounds — not just the room with the problem, and not just the floor it is on.',
        icon: 'broadcast-pin',
        title: 'Sound'
    },{
        copy: 'The alarm names the room. You are not searching a smoke-filled house to work out where it is coming from, and you know whether it is between you and the way out.',
        icon: 'geo-alt-fill',
        title: 'Locate'
    },{
        copy: 'The Bed Shaker shakes the mattress while the hub sends a text, an email and a push notification. It wakes you when you are sleeping and notifies you when you are away.',
        icon: 'phone-fill',
        title: 'Reach you'
    }];

    return (
        <Section id={'how-it-works'}>
            <SectionHeader
            eyebrow={'How it works'}
            subtitle={'Every device talks to every other device over its own private radio network, with no Wi-Fi to drop and no monthly service to lapse. When one of them detects something, all of them sound, and the app tells you which room it started in. That is the difference between waking to a noise somewhere in the house and knowing exactly where to go.'}
            title={'Know in seconds, from anywhere'} />

            {/* The demo carries the weight the in-home advisor's live
                demonstration carries in a living room, so it gets the hero
                slot rather than being tucked under the cards. Nothing
                autoplays — a fire safety video that starts talking at you
                unprompted is the fastest way to get a tab closed. It shows the
                devices themselves, so it isn't shown when the products are
                hidden (see Catalog.isVisible). */}
            {showProducts && (
                <div style={{
                    border: `1px solid ${Theme.colors.border()}`,
                    borderRadius: 16,
                    boxShadow: Theme.boxShadow.card,
                    marginBottom: mobile ? 36 : 56,
                    overflow: 'hidden',
                    paddingTop: '56.25%',
                    position: 'relative',
                    width: '100%'
                }}>
                    <video
                    controls={true}
                    poster={'images/network-video-poster.jpg'}
                    preload={'none'}
                    style={{
                        backgroundColor: Theme.colors.dark(),
                        height: '100%',
                        left: 0,
                        objectFit: 'cover',
                        position: 'absolute',
                        top: 0,
                        width: '100%'
                    }}>
                        <source src={'images/network-video.mp4'} type={'video/mp4'} />
                        {'Your browser does not support embedded video.'}
                    </video>
                </div>
            )}

            <div style={{
                display: 'grid',
                gap: mobile ? 20 : 24,
                gridTemplateColumns: mobile ? '1fr' : 'repeat(4, 1fr)'
            }}>
                {steps.map((step, index) => (
                    <InfoCard key={index} {...step} />
                ))}
            </div>
        </Section>
    )
}

export default HowItWorks;
